package com.merkost.handpass.admin.container

import com.merkost.handpass.admin.api.AdminRepositoryApi
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.admin.contract.ImagesAction
import com.merkost.handpass.admin.contract.ImagesIntent
import com.merkost.handpass.admin.contract.ImagesState
import com.merkost.handpass.admin.platform.WindowFocusTrigger
import com.merkost.handpass.admin.util.toAdminMessage
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import pro.respawn.flowmvi.api.Container
import pro.respawn.flowmvi.api.PipelineContext
import pro.respawn.flowmvi.dsl.lazyStore
import pro.respawn.flowmvi.plugins.recover
import pro.respawn.flowmvi.plugins.reduce

class ImagesContainer(
    private val repo: AdminRepositoryApi,
    private val focusTrigger: WindowFocusTrigger,
) : Container<ImagesState, ImagesIntent, ImagesAction> {

    private val scope = MainScope()

    override val store by lazyStore(ImagesState(), scope) {
        configure { name = "ImagesStore" }
        recover { e ->
            updateState { copy(images = AsyncSection.Failed(e.toAdminMessage())) }
            null
        }
        reduce { intent ->
            when (intent) {
                is ImagesIntent.ReactToFocusTick, ImagesIntent.Refresh -> reloadImages()
                is ImagesIntent.SetFilter -> {
                    updateState { copy(statusFilter = intent.status) }
                    reloadImages()
                }
                is ImagesIntent.Approve -> moderateImage(intent.id) { repo.approveImage(it) }
                is ImagesIntent.Reject -> moderateImage(intent.id) { repo.rejectImage(it) }
            }
        }
    }

    init {
        scope.launch {
            focusTrigger.ticks.collect { tick -> store.intent(ImagesIntent.ReactToFocusTick(tick)) }
        }
    }

    private suspend fun PipelineContext<ImagesState, ImagesIntent, ImagesAction>.currentState(): ImagesState {
        var s = ImagesState()
        withState { s = this }
        return s
    }

    private suspend fun PipelineContext<ImagesState, ImagesIntent, ImagesAction>.reloadImages() {
        val s = currentState()
        val response = repo.getImageQueue(1, 50, s.statusFilter)
        updateState {
            copy(
                images = if (response.error == null) AsyncSection.Loaded(response.data)
                else AsyncSection.Failed(response.error),
            )
        }
    }

    private suspend fun PipelineContext<ImagesState, ImagesIntent, ImagesAction>.moderateImage(
        id: String,
        action: suspend (String) -> Result<Unit>,
    ) {
        updateState { copy(pendingIds = pendingIds + id) }
        val result = action(id)
        runCatching { reloadImages() }.onFailure { e ->
            updateState { copy(images = AsyncSection.Failed(e.toAdminMessage())) }
        }
        result.onFailure { e ->
            updateState { copy(images = AsyncSection.Failed(e.toAdminMessage())) }
        }
        updateState { copy(pendingIds = pendingIds - id) }
    }
}
